import React, { useState } from 'react';
import {
  Select,
  MenuItem,
  FormControl,
  TextField,
  Box,
  IconButton,
  Typography,
  Divider,
  InputAdornment
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { FieldOption } from '../constants/fieldOptions';

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: FieldOption[];
  placeholder?: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  label?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = "Select...",
  disabled = false,
  size = 'small',
  fullWidth = true,
  label
}) => {
  const [isAddingCustom, setIsAddingCustom] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  const handleAddCustom = () => {
    if (customValue.trim()) {
      onChange(customValue.trim());
      setCustomValue('');
      setIsAddingCustom(false);
      setShowCustomInput(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddCustom();
    } else if (e.key === 'Escape') {
      setIsAddingCustom(false);
      setShowCustomInput(false);
      setCustomValue('');
    }
  };

  const isCustomValue = value && !options.find(opt => opt.value === value);

  return (
    <FormControl size={size} fullWidth={fullWidth}>
      <Select
        value={value}
        onChange={(e) => {
          if (e.target.value === '__add_custom__') {
            setIsAddingCustom(true);
            setShowCustomInput(true);
          } else {
            onChange(e.target.value);
          }
        }}
        displayEmpty
        disabled={disabled}
        label={label}
      >
        <MenuItem value="">
          <Typography variant="body2" color="text.secondary">
            {placeholder}
          </Typography>
        </MenuItem>
        
        {/* Standard options */}
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
        
        {/* Custom option separator */}
        <Divider sx={{ my: 0.5 }} />
        
        {/* Add custom option */}
        <MenuItem value="__add_custom__" sx={{ color: 'primary.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon fontSize="small" />
            <Typography variant="body2">Add custom option</Typography>
          </Box>
        </MenuItem>
        
        {/* Show custom value if it exists */}
        {isCustomValue && (
          <MenuItem value={value} sx={{ fontStyle: 'italic', color: 'primary.main' }}>
            "{value}"
          </MenuItem>
        )}
      </Select>
      
      {/* Custom input field */}
      {showCustomInput && (
        <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Enter custom option..."
            value={customValue}
            onChange={(e) => setCustomValue(e.target.value)}
            onKeyPress={handleKeyPress}
            autoFocus
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleAddCustom}
                    disabled={!customValue.trim()}
                    color="primary"
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Box>
      )}
    </FormControl>
  );
};

export default CustomSelect;
